/**
 * MARKETING COMPASS — the quiz (spec §3.1 steps 1-2).
 *
 * Intro → one question per screen with a progress bar and back navigation →
 * hand-off to the results screen (Phase 5).
 *
 * Persistence is per-question via the saveMarketingAnswer callable, so an
 * abandoned quiz resumes exactly where it was left — including across a
 * sign-out and sign-in, because the server keys the response on the auth uid
 * rather than anything held in the browser.
 *
 * Pre-filled answers are pre-selected and clearly labelled, but always
 * editable; changing one clears its pre-fill marker server-side.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { MARKETING_QUIZ, isValidAnswer } from './marketingQuizConfig';
import { prefillAnswers, prefillNote } from './prefill';
import { loadQuizResponse, saveAnswer, completeQuiz, marketingErrorMessage } from './marketingApi';
import * as T from '../brand/tokens';
import { ArrowLeft, ArrowRight, Check, Loader2, Sparkles, AlertCircle } from 'lucide-react';

/* ── Intro ──────────────────────────────────────────────── */

function Intro({ onStart, resuming, prefillCount }) {
  return (
    <div className={T.PANEL}>
      <div className={T.PANEL_INNER}>
        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-gold-soft bg-gold/15 text-gold-deep">
          <Sparkles className="h-5 w-5" />
        </div>
        <h1 className={T.H1_ON_PARCHMENT}>Marketing Compass</h1>
        <p className={"mt-2 text-[17px] leading-relaxed " + T.BODY_MUTED}>
          Answer 16 questions — fewer if we can fill some in from your books — and get a
          marketing tool stack picked for your genre, your stage and your budget. There are
          no wrong answers, and <strong>"I don't know" is always a valid one</strong>: we'll
          tell you what we assumed instead.
        </p>
        {prefillCount > 0 && (
          <p className={"mt-3 rounded-lg border border-gold-soft bg-gold/10 px-3 py-2 text-sm " + T.BODY_MUTED}>
            We've already filled in {prefillCount} answer{prefillCount === 1 ? '' : 's'} from your
            books — check them as you go.
          </p>
        )}
        <button type="button" className={T.BTN_PRIMARY + " mt-5"} onClick={onStart}>
          {resuming ? 'Resume where you left off' : 'Start'} <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ── One question ───────────────────────────────────────── */

function QuestionCard({ q, value, prefilledSource, onChange, saving, freeText, onFreeTextChange }) {
  const isMulti = !!q.multiSelect;
  const selected = isMulti ? (Array.isArray(value) ? value : []) : value;

  const toggle = (optValue) => {
    if (!isMulti) { onChange(optValue); return; }

    const opt = q.options.find((o) => o.value === optValue);
    // "None"/"Not sure" are exclusive — picking one clears the rest, and picking
    // anything else clears them.
    if (opt?.exclusive) { onChange([optValue]); return; }

    const withoutExclusives = selected.filter((v) => !q.options.find((o) => o.value === v)?.exclusive);
    const next = withoutExclusives.includes(optValue)
      ? withoutExclusives.filter((v) => v !== optValue)
      : [...withoutExclusives, optValue];
    onChange(next.length ? next : []);
  };

  const note = prefillNote(q.id, prefilledSource ? { [q.id]: prefilledSource } : null);
  const freeTextOption = q.options.find((o) => o.freeText
    && (isMulti ? selected.includes(o.value) : selected === o.value));

  return (
    <div>
      <h2 className={T.H3 + " text-lg leading-snug"}>{q.question}</h2>
      {isMulti && <p className={"mt-1 text-sm " + T.BODY_FAINT}>Choose as many as apply.</p>}

      {note && (
        <p className={"mt-2 inline-flex items-start gap-1.5 rounded-lg border border-gold-soft bg-gold/10 px-2.5 py-1.5 text-sm " + T.BODY_MUTED}>
          <Check className="mt-0.5 h-3 w-3 shrink-0" /> {note}
        </p>
      )}

      <div className="mt-4 space-y-2">
        {q.options.map((opt) => {
          const showFreeText = opt.freeText
            && (isMulti ? selected.includes(opt.value) : selected === opt.value);
          const isSelected = isMulti ? selected.includes(opt.value) : selected === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={saving}
              aria-pressed={isSelected}
              onClick={() => toggle(opt.value)}
              className={`${T.OPTION} ${isSelected ? T.OPTION_ACTIVE : T.OPTION_IDLE} ${saving ? 'opacity-60' : ''}`}
            >
              <span>{opt.label}</span>
              {isSelected && <Check className="h-4 w-4 shrink-0 text-gold-deep" />}
            </button>
          );
        })}
      </div>

      {/* Free text for an option that declares it (q1 "Other"). The taxonomy
          cannot gate on this, so it never eliminates — it is captured so the
          genre list can grow from real answers. */}
      {freeTextOption && (
        <label className={"mt-3 block text-sm " + T.BODY_FAINT}>
          Which genre? <span className="opacity-70">(we'll add it to our list)</span>
          <input
            type="text"
            maxLength={120}
            value={freeText || ''}
            onChange={(e) => onFreeTextChange && onFreeTextChange(e.target.value)}
            placeholder="e.g. Cosy horror"
            className={T.INPUT}
          />
        </label>
      )}
    </div>
  );
}

/* ── Quiz ───────────────────────────────────────────────── */

export default function MarketingQuiz({ books = [], audiobooks = [], subscriberCount = null, onComplete }) {
  const [stage, setStage] = useState('loading');     // loading | intro | quiz | done | error
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [prefilled, setPrefilled] = useState({});
  // Keyed by question id — only questions with a freeText option ever use it.
  const [freeTexts, setFreeTexts] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Depend on the VALUE of the platform data, not its identity. `books` and
  // `audiobooks` arrive as fresh array literals on every parent render (and the
  // default `[]` params are new objects each time), so an identity-based
  // dependency re-runs this effect forever and keeps resetting the member back
  // to the intro screen mid-quiz.
  const seedKey = JSON.stringify({
    b: (books || []).map((b) => [b?.status, b?.price, b?.coverUrl, (b?.genres || []).join('|')]),
    a: (audiobooks || []).map((a) => a?.status),
    s: subscriberCount,
  });

  // Once the member has started answering, late-arriving platform data must not
  // disturb them — re-seeding would fight their own edits.
  const started = stage === 'quiz' || stage === 'done';

  // Seed from platform data, then let anything already saved server-side win —
  // a resumed answer is the member's own and must not be overwritten by a fresh
  // pre-fill guess.
  useEffect(() => {
    if (started) return undefined;
    let cancelled = false;

    (async () => {
      const seed = prefillAnswers({ books, audiobooks, subscriberCount });
      try {
        const saved = await loadQuizResponse();
        if (cancelled) return;

        if (saved.found) {
          setAnswers({ ...seed.answers, ...saved.answers });
          // `<qid>FreeText` lives alongside the answers; lift it into its own map.
          const ft = {};
          Object.entries(saved.answers || {}).forEach(([k, v]) => {
            if (k.endsWith('FreeText')) ft[k.replace('FreeText', '')] = v;
          });
          setFreeTexts(ft);
          setPrefilled({ ...seed.prefilled, ...saved.prefilled });
          // Resume at the first unanswered question.
          const firstGap = MARKETING_QUIZ.findIndex((q) => saved.answers[q.id] === undefined);
          setIndex(firstGap === -1 ? MARKETING_QUIZ.length - 1 : firstGap);
        } else {
          setAnswers(seed.answers);
          setPrefilled(seed.prefilled);
        }
        setStage('intro');
      } catch (err) {
        if (cancelled) return;
        setError(marketingErrorMessage(err));
        setStage('error');
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey, started]);

  const q = MARKETING_QUIZ[index];
  const answer = q ? answers[q.id] : undefined;
  const answered = q ? isValidAnswer(q.id, answer) : false;

  const persist = useCallback(async (questionId, value, source, freeText) => {
    setSaving(true);
    setError('');
    try {
      await saveAnswer(questionId, value, source, freeText);
      return true;
    } catch (err) {
      setError(marketingErrorMessage(err));
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const handleChange = (value) => {
    if (!q) return;
    setAnswers((prev) => ({ ...prev, [q.id]: value }));
    // A hand-picked answer is no longer pre-filled.
    setPrefilled((prev) => {
      const next = { ...prev };
      delete next[q.id];
      return next;
    });
  };

  const handleNext = async () => {
    if (!q || !answered) return;
    const ok = await persist(q.id, answers[q.id], prefilled[q.id] || null, freeTexts[q.id] || null);
    if (!ok) return;

    if (index < MARKETING_QUIZ.length - 1) {
      setIndex(index + 1);
      return;
    }

    try {
      setSaving(true);
      await completeQuiz();
      setStage('done');
      if (onComplete) onComplete();
    } catch (err) {
      setError(marketingErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (stage === 'loading') {
    return (
      <div className={"flex items-center gap-2 p-6 " + T.ON_INK}>
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your answers…
      </div>
    );
  }

  if (stage === 'error') {
    return (
      <div className={T.PANEL}>
        <div className={T.PANEL_INNER + " flex items-start gap-2.5"}>
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#8a3a1e]" />
          <div>
            <p className="font-semibold text-[#8a3a1e]">{error}</p>
            <button type="button" className={T.BTN_SECONDARY + " mt-3"} onClick={() => window.location.reload()}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'intro') {
    const answeredCount = Object.keys(answers).length;
    return (
      <Intro
        onStart={() => setStage('quiz')}
        resuming={answeredCount > 0 && index > 0}
        prefillCount={Object.keys(prefilled).length}
      />
    );
  }

  if (stage === 'done') {
    return (
      <div className={T.PANEL}>
        <div className={T.PANEL_INNER}>
          <h2 className={T.H1_ON_PARCHMENT}>That's everything — thank you.</h2>
          <p className={"mt-1 text-[17px] " + T.BODY_MUTED}>
            We're putting your tool stack together. Your answers are saved and you can
            change them at any time.
          </p>
        </div>
      </div>
    );
  }

  const progress = ((index + 1) / MARKETING_QUIZ.length) * 100;

  return (
    <div>
      {/* Progress */}
      <div className="mb-4">
        <div className={"mb-1.5 flex items-center justify-between " + T.MICRO + " text-parchment/50"}>
          <span>Question {index + 1} of {MARKETING_QUIZ.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className={T.PROGRESS_TRACK}>
          <div
            className={T.PROGRESS_BAR}
            style={{ width: `${progress}%` }}
            role="progressbar"
            aria-valuenow={index + 1}
            aria-valuemin={1}
            aria-valuemax={MARKETING_QUIZ.length}
          />
        </div>
      </div>

      <div className={T.PANEL}>
        <div className={T.PANEL_INNER}>
          <QuestionCard
            q={q}
            value={answer}
            prefilledSource={prefilled[q.id]}
            onChange={handleChange}
            saving={saving}
            freeText={freeTexts[q.id]}
            onFreeTextChange={(v) => setFreeTexts((prev) => ({ ...prev, [q.id]: v }))}
          />

          {error && (
            <p className="mt-3 flex items-start gap-1.5 text-sm text-[#8a3a1e]">
              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" /> {error}
            </p>
          )}

          {/* Mobile-first: buttons stack full-width, sit side by side from sm. */}
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              className={T.BTN_SECONDARY}
              disabled={index === 0 || saving}
              onClick={() => { setError(''); setIndex(Math.max(0, index - 1)); }}
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
            <button type="button" className={T.BTN_PRIMARY} disabled={!answered || saving} onClick={handleNext}>
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {index === MARKETING_QUIZ.length - 1 ? 'Finish' : 'Next'}
              {!saving && <ArrowRight className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
